import { FC } from 'react';
import { useLoaderData, useNavigate, LoaderFunctionArgs } from 'react-router-dom';
import styled from 'styled-components';

import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import InfoPpvByYear from '../components/ppvInfo_components/InfoPpvByYear';
import colors from '../styles/colors';
import type { PPVSpecific } from '../types';

const MainContainer = styled.div`
  width: 100%;
  padding-top: 28px;
`;

const InfoBasic = styled.div`
  margin: auto;
  text-align: center;
  max-width: 640px;
  padding: 0 16px 28px;

  h1 {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    line-height: 1.6;
    margin: 0;
  }
`;

const BoxInfoActiveBigFour = styled.div`
  gap: 8px;
  margin: 8px 0 16px;
`;

interface BadgeProps {
  $active: boolean;
}

const StatusBadge = styled.div<BadgeProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid ${(props) => (props.$active ? '#28a745' : 'rgba(255,255,255,0.2)')};
  color: ${(props) => (props.$active ? '#28a745' : 'rgba(255,255,255,0.4)')};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${(props) => (props.$active ? '#28a745' : 'rgba(255,255,255,0.25)')};
  }
`;

const BigFourBadge = styled.div<BadgeProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid ${(props) => (props.$active ? 'gold' : 'rgba(255,255,255,0.2)')};
  color: ${(props) => (props.$active ? 'gold' : 'rgba(255,255,255,0.4)')};
`;

const BoxCyclingPpv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

interface PPVDescription {
  name: string;
  is_active: boolean;
  is_big_four: boolean;
  description: string;
  ppvs: PPVSpecific[];
}

const PpvInfo: FC = () => {
  const ppvDescription = useLoaderData() as PPVDescription;
  const navigate = useNavigate();

  return (
    <CommonWrapper>
      <MainContainer className="h-100">
        <InfoBasic>
          <h1 className="nameDescriptionPpv"> {ppvDescription.name} </h1>

          <BoxInfoActiveBigFour className="d-flex justify-content-center">
            <StatusBadge $active={ppvDescription.is_active}>
              {ppvDescription.is_active ? 'Attivo' : 'Inattivo'}
            </StatusBadge>
            <BigFourBadge $active={ppvDescription.is_big_four}>
              {ppvDescription.is_big_four ? 'Big Four' : 'Non Big Four'}
            </BigFourBadge>
          </BoxInfoActiveBigFour>

          <p> {ppvDescription.description} </p>
        </InfoBasic>

        <BoxCyclingPpv className="w-100">
          {ppvDescription.ppvs.map((ppv, index) => (
            <InfoPpvByYear
              key={'ppv-' + ppvDescription.name + '-' + index + '-' + ppv.id}
              id={'ppv-' + ppvDescription.name + '-' + index + '-' + ppv.id}
              ppv={ppv}
              ppvName={ppvDescription.name}
            />
          ))}
        </BoxCyclingPpv>
      </MainContainer>
    </CommonWrapper>
  );
};

export default PpvInfo;

export async function loader({ params }: LoaderFunctionArgs): Promise<PPVDescription> {
  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'api/ppv/' + params.namePpv);
  if (!response.ok) {
    throw new Error('Failed to load PPV info');
  }
  const resData = await response.json();
  return resData;
}
